import React, {
	useEffect,
	useState,
	useCallback,
	type PropsWithChildren,
} from 'react';
import _ from 'underscore';
import {i18n} from '@netx/core-next/locales';
import {createTheme, type PaletteMode} from '@mui/material';
import StylesEngineProvider from '@mui/material/StyledEngineProvider';
import {ThemeProvider} from '@mui/material/styles';
import {StylesProvider} from '@mui/styles';
import {getThemeMode} from '@netx/core/css';
import {useDispatcher} from '@netx/core-components/hooks/useDispatcher';
import {getComponentOverrides} from './utils/getComponentOverrides';
import {getThemeMixins} from './utils/getThemeMixins';
import {getThemePalette} from './utils/getThemePalette';
import type {Theme} from './types';

export const baseTheme = (type: PaletteMode = 'light', demo = false) =>
	createTheme(
		{
			mixins: getThemeMixins(type, {demo}),
			palette: getThemePalette(type, {demo}),
			components: getComponentOverrides(type, {demo}),
		},
		[i18n.language],
	);

/** netx theme */
const NetxTheme = ({children}: PropsWithChildren<{}>) => {
	const [theme, setTheme] = useState<Theme>(() => baseTheme(getThemeMode()));

	useDispatcher(
		'theme:toggled',
		useCallback((themeMode: PaletteMode) => setTheme(baseTheme(themeMode)), []),
	);

	useDispatcher(
		'theme:toggled',
		useCallback((themeMode: PaletteMode) => setTheme(baseTheme(themeMode)), []),
	);

	useDispatcher(
		'css:variable:updated',
		useCallback(
			_.debounce(() => setTheme(baseTheme(getThemeMode())), 150),
			[],
		),
	);

	useEffect(() => {
		const listener = (lng: string) => setTheme(baseTheme(getThemeMode()));
		i18n.on('languageChanged', listener);
		return () => {
			i18n.off('languageChanged', listener);
		};
	}, []);

	return (
		<StylesProvider>
			<ThemeProvider theme={theme}>
				<StylesEngineProvider injectFirst>{children}</StylesEngineProvider>
			</ThemeProvider>
		</StylesProvider>
	);
};

export default NetxTheme;
